import { useEffect } from 'react';

export interface CancelChargePreview {
  will_charge: boolean;
  charge_amount: number;
  refund_amount: number;
  currency: string;
  window: 'FULL_REFUND' | 'PARTIAL' | 'NO_REFUND' | 'NONE';
}

interface CancelBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  // Optional — only the customer flow passes it. When the cancellation
  // falls inside a charge window we show what they'll lose vs. get back.
  preview?: CancelChargePreview | null;
  previewLoading?: boolean;
}

const formatMoney = (amount: number, currency: string): string => {
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
};

export function CancelBookingModal({
  isOpen,
  onClose,
  onConfirm,
  preview,
  previewLoading,
}: CancelBookingModalProps) {
  // Prevent scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/30 backdrop-blur-[2px] animate-in fade-in duration-300" 
        onClick={onClose}
      />
      
      {/* Modal Container */}
      <div className="relative bg-white rounded-[20px] shadow-2xl w-full max-w-[360px] overflow-hidden animate-in fade-in zoom-in duration-300">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <div className="w-8" /> {/* Spacer for centering */}
            <h2 className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[18px] text-center flex-1">
              Cancel booking
            </h2>
            <button 
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#f4f4f4] transition-colors"
            >
              <span className="material-icons text-[#3a3335] text-[20px]">close</span>
            </button>
          </div>

          <div className="h-[1px] bg-[#ede8e9] w-full mb-6" />

          {/* Message */}
          <div className="text-center mb-6">
            <h3 className="font-['DM_Sans',sans-serif] font-medium text-[#3a3335] text-[15px] leading-[24px] tracking-[-0.1px]">
              Are you sure you want to<br />cancel your booking?
            </h3>
          </div>

          {/* Charge / refund warning — shown when the cancellation falls
              inside a charge window so there's no surprise on the card. */}
          {previewLoading ? (
            <div className="mb-6 text-center font-['DM_Sans',sans-serif] text-[#908e8e] text-[13px]">
              Checking the cancellation policy…
            </div>
          ) : preview && preview.will_charge ? (
            <div className="mb-6 rounded-[12px] bg-[#fcf3f3] border border-[#f0d6d6] px-4 py-3">
              <p className="font-['DM_Sans',sans-serif] font-bold text-[#8e2a2f] text-[14px] mb-1">
                {preview.window === 'NO_REFUND'
                  ? 'This cancellation is non-refundable'
                  : 'A cancellation charge applies'}
              </p>
              <p className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[13px] leading-[20px]">
                {preview.window === 'NO_REFUND' ? (
                  <>
                    You&apos;re cancelling within the no-refund window, so the
                    full{' '}
                    <span className="font-bold">
                      {formatMoney(preview.charge_amount, preview.currency)}
                    </span>{' '}
                    is non-refundable and goes to your professional.
                  </>
                ) : (
                  <>
                    You&apos;ll be refunded{' '}
                    <span className="font-bold">
                      {formatMoney(preview.refund_amount, preview.currency)}
                    </span>{' '}
                    to your card.{' '}
                    <span className="font-bold">
                      {formatMoney(preview.charge_amount, preview.currency)}
                    </span>{' '}
                    is non-refundable and goes to your professional.
                  </>
                )}
              </p>
            </div>
          ) : preview && !preview.will_charge && preview.refund_amount > 0 ? (
            <div className="mb-6 rounded-[12px] bg-[#f3f8f3] border border-[#d6ead6] px-4 py-3 text-center">
              <p className="font-['DM_Sans',sans-serif] text-[#2f6e3a] text-[13px] leading-[20px]">
                You&apos;ll be fully refunded{' '}
                <span className="font-bold">
                  {formatMoney(preview.refund_amount, preview.currency)}
                </span>{' '}
                to your card.
              </p>
            </div>
          ) : null}

          <div className="h-[1px] bg-[#ede8e9] w-full mb-6" />

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onConfirm}
              className="flex-1 h-[40px] bg-[#eee] rounded-[12px] font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-sm hover:bg-[#e2e2e2] transition-all active:scale-[0.98] flex items-center justify-center"
            >
              Yes, Cancel
            </button>
            <button
              onClick={onClose}
              className="flex-1 h-[40px] bg-[#fcfaf7] rounded-[12px] font-['DM_Sans',sans-serif] font-bold text-[#8e2a2f] text-sm hover:bg-[#f2eee2] transition-all active:scale-[0.98] flex items-center justify-center"
            >
              Keep Booking
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
